import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'sonner';
import {
  LiveKitRoom,
  VideoConference,
} from '@livekit/components-react';
import '@livekit/components-styles';
import './LiveKitCall.css'; // Customizações visuais (remover labels + responsividade)
import { Loader2 } from 'lucide-react';

const API = '/api';

/**
 * LiveKitCall component - Handles video call integration with LiveKit
 * 
 * Props:
 * - roomName: Name of the LiveKit room (e.g., "sessao-123")
 * - role: User role - "medico" or "ator"
 * - sessaoId: Session ID for API calls
 */
const LiveKitCall = ({ roomName, role, sessaoId }) => {
  const [token, setToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    fetchToken();
  }, [roomName, role, sessaoId]);

  const fetchToken = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('═══════════════════════════════════════════');
      console.log('[LIVEKIT FRONTEND] 🚀 STARTING TOKEN REQUEST');
      console.log('═══════════════════════════════════════════');
      console.log('[LIVEKIT FRONTEND] Room Name:', roomName);
      console.log('[LIVEKIT FRONTEND] Role:', role);
      console.log('[LIVEKIT FRONTEND] Session ID:', sessaoId);
      console.log('[LIVEKIT FRONTEND] API Endpoint:', `${API}/livekit/token`);
      
      const requestPayload = {
        roomName,
        role,
      };
      
      console.log('[LIVEKIT FRONTEND] Request payload:', JSON.stringify(requestPayload, null, 2));
      console.log('[LIVEKIT FRONTEND] Sending POST request...');
      
      const response = await axios.post(`${API}/livekit/token`, requestPayload);
      
      console.log('[LIVEKIT FRONTEND] ✅ Response received!');
      console.log('[LIVEKIT FRONTEND] Status:', response.status);
      console.log('[LIVEKIT FRONTEND] Response data:', response.data);
      console.log('[LIVEKIT FRONTEND] Token preview:', response.data.token?.substring(0, 50) + '...');
      console.log('[LIVEKIT FRONTEND] Token length:', response.data.token?.length);
      console.log('[LIVEKIT FRONTEND] LiveKit URL:', response.data.url);
      
      // Decode JWT to inspect (for debugging only)
      try {
        const tokenParts = response.data.token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log('[LIVEKIT FRONTEND] 🔍 Decoded token payload:');
          console.log('[LIVEKIT FRONTEND]   - sub (identity):', payload.sub);
          console.log('[LIVEKIT FRONTEND]   - name:', payload.name);
          console.log('[LIVEKIT FRONTEND]   - video.room:', payload.video?.room);
          console.log('[LIVEKIT FRONTEND]   - video.roomJoin:', payload.video?.roomJoin);
          console.log('[LIVEKIT FRONTEND]   - video.canPublish:', payload.video?.canPublish);
          console.log('[LIVEKIT FRONTEND]   - video.canSubscribe:', payload.video?.canSubscribe);
          console.log('[LIVEKIT FRONTEND]   - exp (expiration):', payload.exp, 
            payload.exp ? new Date(payload.exp * 1000).toISOString() : 'N/A');
          console.log('[LIVEKIT FRONTEND]   - iss (issuer/api_key):', payload.iss);
          console.log('[LIVEKIT FRONTEND] Full payload:', payload);
        }
      } catch (e) {
        console.warn('[LIVEKIT FRONTEND] Could not decode token:', e.message);
      }
      
      setToken(response.data.token);
      setLivekitUrl(response.data.url);
      
      setDebugInfo({
        roomName,
        role,
        url: response.data.url,
        tokenLength: response.data.token?.length,
        timestamp: new Date().toISOString(),
      });
      
      console.log('[LIVEKIT FRONTEND] State updated, will connect to LiveKit server...');
      
    } catch (err) {
      console.error('═══════════════════════════════════════════');
      console.error('[LIVEKIT FRONTEND] ❌ ERROR FETCHING TOKEN');
      console.error('═══════════════════════════════════════════');
      console.error('[LIVEKIT FRONTEND] Error object:', err);
      console.error('[LIVEKIT FRONTEND] Error message:', err.message);
      console.error('[LIVEKIT FRONTEND] Response status:', err.response?.status);
      console.error('[LIVEKIT FRONTEND] Response data:', err.response?.data);
      console.error('[LIVEKIT FRONTEND] Response headers:', err.response?.headers);
      
      let errorMessage = 'Erro desconhecido ao conectar à chamada';
      
      if (err.response) {
        // Server responded with error
        errorMessage = err.response.data?.detail || `Erro ${err.response.status}: ${err.response.statusText}`;
        console.error('[LIVEKIT FRONTEND] Server error detail:', errorMessage);
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'Sem resposta do servidor. Verifique sua conexão.';
        console.error('[LIVEKIT FRONTEND] No response from server');
      } else {
        // Error in request setup
        errorMessage = err.message;
        console.error('[LIVEKIT FRONTEND] Request setup error:', err.message);
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      
      console.error('═══════════════════════════════════════════');
    } finally {
      setLoading(false);
      console.log('[LIVEKIT FRONTEND] Loading state set to false');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-black rounded-lg">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-3" />
          <p className="text-white text-sm">Conectando à chamada...</p>
          <p className="text-gray-400 text-xs mt-2">Room: {roomName}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-red-900/20 rounded-lg border-2 border-red-500">
        <div className="text-center px-4 max-w-md">
          <p className="text-red-400 font-medium mb-2 text-lg">❌ Erro na chamada</p>
          <p className="text-red-300 text-sm mb-2 whitespace-pre-wrap break-words">{error}</p>
          <p className="text-gray-400 text-xs mb-4">
            Abra o Console do navegador (F12) para mais detalhes
          </p>
          <button
            onClick={fetchToken}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // Connected state
  if (token && livekitUrl) {
    console.log('═══════════════════════════════════════════');
    console.log('[LIVEKIT FRONTEND] 🎥 RENDERING LIVEKITROOM');
    console.log('═══════════════════════════════════════════');
    console.log('[LIVEKIT FRONTEND] Server URL:', livekitUrl);
    console.log('[LIVEKIT FRONTEND] Room Name (from props):', roomName);
    console.log('[LIVEKIT FRONTEND] Role:', role);
    console.log('[LIVEKIT FRONTEND] Token length:', token?.length);
    console.log('[LIVEKIT FRONTEND] Token (first 80 chars):', token?.substring(0, 80));
    console.log('[LIVEKIT FRONTEND] IMPORTANT: The room name is embedded in the token');
    console.log('[LIVEKIT FRONTEND] LiveKitRoom will connect using the token, not roomName prop');
    console.log('═══════════════════════════════════════════');
    
    return (
      <div className="livekit-room-container h-full rounded-lg overflow-hidden">
        <LiveKitRoom
          video={true}
          audio={true}
          token={token}
          serverUrl={livekitUrl}
          connect={true}
          data-lk-theme="default"
          style={{ height: '100%', minHeight: '300px' }}
          onConnected={() => {
            console.log('═══════════════════════════════════════════');
            console.log('[LIVEKIT FRONTEND] ✅ CONNECTED TO ROOM!');
            console.log('═══════════════════════════════════════════');
            console.log('[LIVEKIT FRONTEND] Room:', roomName);
            console.log('[LIVEKIT FRONTEND] URL:', livekitUrl);
            console.log('[LIVEKIT FRONTEND] Role:', role);
            console.log('═══════════════════════════════════════════');
            toast.success('Conectado à chamada de vídeo!');
          }}
          onDisconnected={(reason) => {
            console.log('═══════════════════════════════════════════');
            console.log('[LIVEKIT FRONTEND] ⚠️ DISCONNECTED FROM ROOM');
            console.log('═══════════════════════════════════════════');
            console.log('[LIVEKIT FRONTEND] Room:', roomName);
            console.log('[LIVEKIT FRONTEND] Reason:', reason);
            console.log('═══════════════════════════════════════════');
            toast.info('Desconectado da chamada');
          }}
          onError={(error) => {
            console.error('═══════════════════════════════════════════');
            console.error('[LIVEKIT FRONTEND] ❌ LIVEKIT ROOM ERROR');
            console.error('═══════════════════════════════════════════');
            console.error('[LIVEKIT FRONTEND] Error object:', error);
            console.error('[LIVEKIT FRONTEND] Error type:', typeof error);
            console.error('[LIVEKIT FRONTEND] Error constructor:', error?.constructor?.name);
            console.error('[LIVEKIT FRONTEND] Error name:', error?.name);
            console.error('[LIVEKIT FRONTEND] Error message:', error?.message);
            console.error('[LIVEKIT FRONTEND] Error code:', error?.code);
            console.error('[LIVEKIT FRONTEND] Error reason:', error?.reason);
            console.error('[LIVEKIT FRONTEND] Error stack:', error?.stack);
            console.error('[LIVEKIT FRONTEND] Room (from props):', roomName);
            console.error('[LIVEKIT FRONTEND] Server URL:', livekitUrl);
            console.error('[LIVEKIT FRONTEND] Token length:', token?.length);
            console.error('═══════════════════════════════════════════');
            console.error('[LIVEKIT FRONTEND] Full error object keys:', Object.keys(error || {}));
            console.error('[LIVEKIT FRONTEND] Full error JSON:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
            console.error('═══════════════════════════════════════════');
            
            toast.error(`Erro na chamada: ${error?.message || 'Erro desconhecido'}`);
          }}
        >
          {/* Video tiles */}
          <VideoConference />
        </LiveKitRoom>
      </div>
    );
  }

  return null;
};

export default LiveKitCall;
